import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { KeyRound, UserPlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only settings panel:
 *   1. Change your own admin password (POST /api/auth/change-password)
 *   2. Create or promote another user to admin (POST /api/admin/users/promote)
 *
 * Rendered inline on the Profile page below the Admin Panel + Changelog buttons.
 */
export const AdminSettings = () => {
  const authHeaders = () => {
    const t = localStorage.getItem('session_token');
    return {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    };
  };

  // ── Change password ──
  const [cp, setCp] = useState({ current: '', next: '', confirm: '' });
  const [cpBusy, setCpBusy] = useState(false);

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (cp.next !== cp.confirm) return toast.error('New passwords do not match');
    if (cp.next.length < 8) return toast.error('New password must be at least 8 characters');
    setCpBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ current_password: cp.current, new_password: cp.next }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Failed to change password');
      toast.success('Password changed');
      setCp({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCpBusy(false);
    }
  };

  // ── Promote / create admin ──
  const [pr, setPr] = useState({ email: '', password: '', is_admin: true });
  const [prBusy, setPrBusy] = useState(false);

  const submitPromote = async (e) => {
    e.preventDefault();
    if (!pr.email) return toast.error('Email is required');
    setPrBusy(true);
    try {
      const body = { email: pr.email, is_admin: pr.is_admin };
      if (pr.password) body.password = pr.password;
      const res = await fetch(`${API_URL}/api/admin/users/promote`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Failed to update user');
      toast.success(
        d.action === 'created'
          ? `Created admin ${d.email}`
          : `Updated ${d.email} (is_admin=${d.is_admin})`,
      );
      setPr({ email: '', password: '', is_admin: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPrBusy(false);
    }
  };

  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2">
      <Card className="bg-[#0b1220] border-[#1e2a45]" data-testid="admin-change-password-card">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#F97316]" />
            Change my password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitPasswordChange} className="space-y-3">
            <div>
              <Label className="text-xs text-gray-400">Current password</Label>
              <Input
                type="password" autoComplete="current-password" required
                value={cp.current} onChange={(e) => setCp({ ...cp, current: e.target.value })}
                data-testid="admin-cp-current"
                className="bg-[#050810] border-[#1e2a45] text-white"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">New password (min 8 chars)</Label>
              <Input
                type="password" autoComplete="new-password" required minLength={8}
                value={cp.next} onChange={(e) => setCp({ ...cp, next: e.target.value })}
                data-testid="admin-cp-new"
                className="bg-[#050810] border-[#1e2a45] text-white"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Confirm new password</Label>
              <Input
                type="password" autoComplete="new-password" required
                value={cp.confirm} onChange={(e) => setCp({ ...cp, confirm: e.target.value })}
                data-testid="admin-cp-confirm"
                className="bg-[#050810] border-[#1e2a45] text-white"
              />
            </div>
            <Button type="submit" disabled={cpBusy} data-testid="admin-cp-submit"
              className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white">
              {cpBusy ? 'Saving…' : 'Change password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-[#0b1220] border-[#1e2a45]" data-testid="admin-promote-card">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-[#3B82F6]" />
            Create / promote admin
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitPromote} className="space-y-3">
            <div>
              <Label className="text-xs text-gray-400">User email</Label>
              <Input
                type="email" autoComplete="off" required
                placeholder="teammate@example.com"
                value={pr.email} onChange={(e) => setPr({ ...pr, email: e.target.value })}
                data-testid="admin-pr-email"
                className="bg-[#050810] border-[#1e2a45] text-white"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">
                Password
                <span className="text-gray-500 ml-1">
                  (required for new users; leave blank to only flip admin flag)
                </span>
              </Label>
              <Input
                type="password" autoComplete="new-password" minLength={8}
                placeholder="Min 8 chars"
                value={pr.password} onChange={(e) => setPr({ ...pr, password: e.target.value })}
                data-testid="admin-pr-password"
                className="bg-[#050810] border-[#1e2a45] text-white"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={pr.is_admin}
                onChange={(e) => setPr({ ...pr, is_admin: e.target.checked })}
                data-testid="admin-pr-isadmin"
                className="h-4 w-4 accent-[#3B82F6]"
              />
              Grant admin privileges
            </label>
            <Button type="submit" disabled={prBusy} data-testid="admin-pr-submit"
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white">
              {prBusy ? 'Saving…' : 'Save user'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;
